import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * ConfirmModal - A simple and clean confirmation dialog for destructive actions.
 * Matches the style used in DatabaseConnection delete modal.
 */
export const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    loading = false,
    showWarning = true,
    confirmButtonVariant = "danger", // "danger" for delete (red), "primary" for create (blue)
}) => {
    const { t } = useTranslation(['ui-complex', 'common']);
    if (!isOpen) return null;

    const resolvedTitle = title ?? t('ui-complex:confirmModal.title');
    const resolvedConfirmText = confirmText ?? t('common:delete');
    const resolvedCancelText = cancelText ?? t('common:cancel');

    const confirmButtonClasses = confirmButtonVariant === "danger"
        ? "bg-red-600 hover:bg-red-700"
        : "bg-primary-600 hover:bg-primary-700";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={loading ? undefined : onClose}>
            <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                        {showWarning && (
                            <div className="flex-shrink-0">
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            </div>
                        )}
                        <div className="flex-1">
                            {resolvedTitle && <h3 className="text-lg font-bold text-gray-900 mb-2">{resolvedTitle}</h3>}
                            <div className="text-gray-700">{message}</div>
                        </div>
                        {!loading && (
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50"
                        >
                            {resolvedCancelText}
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className={`px-4 py-2 text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 flex items-center gap-2 ${confirmButtonClasses}`}
                        >
                            {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                            {resolvedConfirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
